import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';

// Firebase's own exported `analytics` binding starts null and is only set
// once isSupported() resolves (async, see firebase.js) — reading it INSIDE
// this function, rather than destructuring it once at import time, is what
// lets every call site correctly see the real instance once it's ready
// (ES module bindings are live, so the import above stays in sync with
// firebase.js's later reassignment) instead of being permanently stuck with
// whatever `analytics` happened to be at the moment this module first
// loaded. Wrapped in try/catch so an analytics hiccup (ad blocker, private
// browsing, a malformed param) can never break the actual feature it's
// attached to.
function trackEvent(eventName, params) {
  try {
    if (!analytics) return;
    logEvent(analytics, eventName, params);
  } catch (e) {
    // Analytics is diagnostic, never load-bearing — silently drop.
  }
}

// Named wrappers for the actual acquisition/conversion funnel this app
// otherwise had zero visibility into (landing page -> demo/signup -> trial
// checkout) — added specifically because Firebase Analytics was previously
// initialized but never actually called anywhere in the app. Event names
// matching GA4's own "recommended events" (sign_up, login, begin_checkout)
// are used where they map cleanly, so they show up in Firebase/GA4's
// standard funnel reports instead of only as raw custom events.
export function trackSignUp(method = 'email') {
  trackEvent('sign_up', { method });
}

export function trackLogin(method = 'email') {
  trackEvent('login', { method });
}

export function trackDemoStart() {
  trackEvent('demo_start');
}

export function trackTrialCtaClick(source) {
  trackEvent('trial_cta_click', { source });
}

export function trackBeginCheckout({ billingCycle, extraSeats, forcedGate, skipTrial, value }) {
  trackEvent('begin_checkout', {
    // currency/value are GA4's own recommended params for this event —
    // without them the event still counts (fine for marking it a key
    // event/conversion), but GA4's Conversions/Monetization reports show no
    // revenue figure attached to it. value is the actual monthly-equivalent
    // price for the plan/seat count being checked out with (see
    // UpgradeModal.jsx's totalMonthlyEquivalent) — display pricing, not a
    // guarantee of the exact Stripe charge (e.g. skip_trial bills
    // immediately at this amount, a normal trial doesn't charge until day
    // 14), but it's the right figure for "what conversion was this worth."
    currency: 'USD',
    value,
    billing_cycle: billingCycle,
    extra_seats: extraSeats,
    // Distinguishes the mandatory post-signup trial gate from a
    // deliberate, later "Upgrade" click by an already-trialing/free user —
    // same checkout call either way, but a materially different funnel
    // step worth telling apart in reporting.
    forced_gate: !!forcedGate,
    // "Buy Now" — bypassed the 14-day trial and billed immediately.
    skip_trial: !!skipTrial,
  });
}

export default trackEvent;
